import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "@mui/material";
import { styles } from "./styles/dataTableMD/dataTableMD.jsx";
import { useTheme } from "@mui/material/styles";
import DataGridHeader from "./components/dataTableMD/dataTableHeader.js";
import DataTableFilters from "./components/dataTableMD/dataTableFilters.js";
import DataGridFooter from "./components/dataTableMD/dataTableFooter.js";
import DataTable from "./components/dataTableMD/dataTable.js";
import { api } from "../../api/index";
import { LoadingIcon } from "./loadingIcon.js";
import { WebSocketContext } from "../context/websocketsContext.js";

// De-duplicate rows by `id`, keeping the LAST occurrence's data in the first-seen position.
// Guards against duplicate incident IDs when a websocket-pushed row later also arrives in a
// server page (`[...data, ...page]`), or when the same id spans paginated appends.
const dedupeById = (rows) => {
  if (!Array.isArray(rows)) return rows;
  const map = new Map();
  for (const row of rows) {
    const key = row && row.id !== undefined ? row.id : row;
    if (map.has(key)) {
      // Merge latest field values onto the existing entry, keeping its position.
      map.set(key, { ...map.get(key), ...row });
    } else {
      map.set(key, row);
    }
  }
  return Array.from(map.values());
};

// Whether a typed column filter value actually constrains anything (FEAT-031).
const isColumnFilterActive = (val, type) => {
  if (val === undefined || val === null) return false;
  if (type === "select") return Array.isArray(val) && val.length > 0;
  if (type === "date") {
    if (typeof val !== "object") return false;
    if (val.mode === "relative") return !!val.days;
    if (val.mode === "range") return !!(val.start || val.end);
    if (val.mode === "exact") return !!val.date;
    return false;
  }
  return typeof val === "string" && val.trim() !== "";
};

// Does a loaded row satisfy a typed column filter?
const rowMatchesColumnFilter = (row, field, val, type) => {
  const raw = row[field];
  if (type === "select") {
    const rowVals = String(raw === undefined || raw === null ? "" : raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return val.some((sel) => rowVals.includes(sel));
  }
  if (type === "date") {
    if (!raw) return false;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) return false;
    if (val.mode === "relative") return t >= Date.now() - val.days * 86400000;
    if (val.mode === "range") {
      const s = val.start ? new Date(val.start + "T00:00:00").getTime() : -Infinity;
      const e = val.end ? new Date(val.end + "T23:59:59").getTime() : Infinity;
      return t >= s && t <= e;
    }
    if (val.mode === "exact") {
      return new Date(raw).toDateString() === new Date(val.date + "T00:00:00").toDateString();
    }
    return true;
  }
  return String(raw === undefined || raw === null ? "" : raw)
    .toLowerCase()
    .includes(String(val).trim().toLowerCase());
};

const DataTableMD = ({
  title,
  dbTable,
  filters,
  toggles,
  columns,
  addButton,
  addSuccessMessage,
  sort,
  isTab,
  apiBody,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [apiCalls, setApiCalls] = useState({});
  const [apiCallCount, setApiCallCount] = useState(0);
  const [activeToggles, setActiveToggles] = useState(toggles);
  const [page, setPage] = useState(1);
  // Default page size fits the viewport but never drops below 25 (FEAT-032 — the old
  // window-derived value could be as low as ~5, which the team found far too few).
  const computePageSize = () => {
    const computed = parseInt((window.innerHeight - 400) / 75, 10);
    return Math.max(25, Number.isNaN(computed) ? 25 : computed);
  };
  const [pageSize, setPageSize] = useState(computePageSize());
  const [pageSizes, setPageSizes] = useState(() =>
    Array.from(new Set([computePageSize(), 25, 50, 100, 200])).sort((a, b) => a - b)
  );
  // Per-column client-side sort + filter over the loaded rows (FEAT-031).
  const [sortConfig, setSortConfig] = useState(null); // { field, dir: "asc" | "desc" }
  const [columnFilters, setColumnFilters] = useState({}); // { [field]: text }
  const didMountResizeRef = useRef(false);
  // When sort/filter resets to page 0, the page-change effect must NOT refetch from the server
  // (that was wiping the accumulated list down to the last server page). This ref tells that
  // effect to skip exactly one run triggered by a client-side sort/filter page reset.
  const skipNextPageFetchRef = useRef(false);
  const [totalPages, setTotalPages] = useState(0);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [subscribe, unsubscribe] = useContext(WebSocketContext);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  const theme = useTheme();
  const style = styles(theme);

  useEffect(() => {
    for (let call in apiCalls){
      if (apiCalls[call].count < apiCallCount){
        const tempApiCalls = apiCalls;
        tempApiCalls[call].val = false;
        setApiCalls({...tempApiCalls});
      }
    }
    // eslint-disable-next-line
  }, [apiCallCount]);

  const fetchData = async () => {
    setTotalCount(0);
    setData([]);
    const apiCallId = (Math.random()*16).toString(16);
    const tempApiCalls = apiCalls;
    if (loading){
      tempApiCalls[apiCallId] = {count: apiCallCount + 1, val: true};
      setApiCallCount(apiCallCount + 1);
    } else {
      tempApiCalls[apiCallId] = {count: 0, val: true};;
      setApiCallCount(0);
    }
    setApiCalls({...tempApiCalls});
    setLoading(true);
    setLoaded(false);
    let currentApiBody = {};
    if (apiBody){
      currentApiBody = JSON.parse(JSON.stringify(apiBody));
    }
    currentApiBody.pageSize = pageSize;
    if (lastEvaluatedKey) {
      if (Object.keys(filterData).length > 0) {
        setLastEvaluatedKey(null);
      } else {
        currentApiBody.pageKey = lastEvaluatedKey;
      }
    }
    for (let field in filterData) {
      const tempFilterDict = {
        key: field,
        value: filterData[field],
        expression: "=",
      };
      if (!currentApiBody.filters) {
        currentApiBody.filters = [];
      }
      currentApiBody.filters.push(tempFilterDict);
    }
    const finalApiBody = {...currentApiBody};
    if (currentApiBody.filters){
      finalApiBody.filters = JSON.stringify(currentApiBody.filters);
    }
    try {
      const response = await api("get", finalApiBody, dbTable);

      if (response.status === 200) {
        if (tempApiCalls[apiCallId].val){
          let tempLastEvaluatedKey = null;
          if (response.data.LastEvaluatedKey) {
            tempLastEvaluatedKey = JSON.stringify(response.data.LastEvaluatedKey);
            setLastEvaluatedKey(tempLastEvaluatedKey);
          } else {
            setLastEvaluatedKey(null);
          }

          let tempData = response.data.data;
          if (tempLastEvaluatedKey) {
            tempData = [...data, ...response.data.data];
          }
          tempData = dedupeById(tempData);
          setData([...tempData]);
          setCurrentPageData([...tempData]);
          setTotalCount(response.data.TotalCount);
        }
      }
    } catch (error) {
    } finally {
      if (tempApiCalls[apiCallId].val){
        setLoaded(true);
        setLoading(false);
      }
    }
    const tempApiCalls2 = apiCalls;
    delete tempApiCalls2[apiCallId];
    setApiCalls({...tempApiCalls});
  };

  useEffect(() => {
    // A client-side sort/filter page reset must not refetch (it would replace the accumulated
    // list with a single server page). Skip exactly one such run.
    if (skipNextPageFetchRef.current) {
      skipNextPageFetchRef.current = false;
      return;
    }
    const fetchDataFunc = async () => {
      if (!loading) {
        await fetchData()
      }
    }
    // Only fetch MORE from the server when the user has actually paged to the last loaded page
    // and the server still has rows. (The old `lastEvaluatedKey && pageSize > 10` clause fired on
    // every page change — including sort/filter resets — and wiped the list. Removed.)
    const loadedPages = Math.max(1, Math.ceil(data.length / pageSize));
    if (lastEvaluatedKey && page >= loadedPages - 1 && data.length > 0) {
      fetchDataFunc();
    }
    // eslint-disable-next-line
  }, [page, pageSize]);

  useEffect(() => {
    subscribe(dbTable, (messageData) => {
      const tempData = [...data];
      if (messageData.command === "newIncident") {
        const incidentData = messageData.incident;
        // Only add if not already present (a newIncident broadcast can arrive for an incident
        // already loaded from the server -> otherwise it renders as a duplicate ID).
        const exists = tempData.some((record) => record.id === incidentData.id);
        if (!exists) {
          tempData.push(incidentData);
        }
      } else if (messageData.command === "incidentUpdated") {
        const incidentData = messageData.incident;
        const index = tempData.findIndex(
          (record) => record.id === incidentData.id
        );
        if (index !== -1) {
          const tempIncident = tempData[index];
          for (let field in incidentData) {
            if (field !== "id") {
              tempIncident[field] = incidentData[field];
            }
          }
          tempData[index] = tempIncident;
        }
      }
      setData(dedupeById(tempData));
    });
    return () => {
      unsubscribe(dbTable);
    };
  }, [data, subscribe, unsubscribe, dbTable]);

  const processFilter = async (field, values) => {
    const tempFilterData = filterData;
    if (field && (values || values === null)) {
      if (!Array.isArray(values)) {
        if (values){
          values = [values];
        } else {
          values = [];
        }
      }
      if (activeToggles) {
        if (activeToggles.field === field) {
          const tempActiveToggles = activeToggles;
          tempActiveToggles.active = values;
          setActiveToggles(tempActiveToggles);
        }
      }
      if (values.includes("All") || values.length === 0) {
        for (let filter in filters) {
          if (filters[filter].apiConfig && filters[filter].apiConfig.requires){
            if (filters[filter].apiConfig.requires.includes(field)){
              delete tempFilterData[filters[filter].field];
            }
          }
        }

        delete tempFilterData[field];
      } else {
        tempFilterData[field] = values;
      }
      setFilterData({...tempFilterData});
    }
  };

  const handleRowClick = async (id) => {
    const formattedTitle = title.replace(/s$/, "").replace(" ", "");
    const lowercaseTitle = formattedTitle.toLowerCase();
    const thisRecord = data.filter((record) => record.id === id);
    navigate(`/${lowercaseTitle}/${id}`, { state: thisRecord[0] });
  };

  // Cycle a column's sort: none -> asc -> desc -> none. Client-side over loaded rows.
  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (!prev || prev.field !== field) return { field, dir: "asc" };
      if (prev.dir === "asc") return { field, dir: "desc" };
      return null;
    });
    if (page !== 0) skipNextPageFetchRef.current = true;
    setPage(0);
  };

  const handleColumnFilter = (field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    if (page !== 0) skipNextPageFetchRef.current = true;
    setPage(0);
  };

  // Map of field -> filterType, and derived multi-select options (unique values in loaded rows).
  const fieldTypeMap = useMemo(() => {
    const m = {};
    (columns || []).forEach((c) => { m[c.field] = c.filterType || "text"; });
    return m;
  }, [columns]);

  const filterOptionsMap = useMemo(() => {
    const m = {};
    (columns || []).forEach((c) => {
      if (c.filterType === "select") {
        const set = new Set();
        data.forEach((row) => {
          const raw = row[c.field];
          if (raw === undefined || raw === null || raw === "") return;
          String(raw).split(",").map((s) => s.trim()).filter(Boolean).forEach((v) => set.add(v));
        });
        m[c.field] = Array.from(set).sort();
      }
    });
    return m;
  }, [columns, data]);

  const hasColumnFilters = Object.keys(columnFilters).some((f) =>
    isColumnFilterActive(columnFilters[f], fieldTypeMap[f] || "text")
  );

  // Filtered + sorted view of the loaded rows (client-side, FEAT-031).
  const derivedData = useMemo(() => {
    let out = data;
    const activeCols = Object.keys(columnFilters).filter((f) =>
      isColumnFilterActive(columnFilters[f], fieldTypeMap[f] || "text")
    );
    if (activeCols.length) {
      out = out.filter((row) =>
        activeCols.every((f) => rowMatchesColumnFilter(row, f, columnFilters[f], fieldTypeMap[f] || "text"))
      );
    }
    if (sortConfig && sortConfig.field) {
      const { field, dir } = sortConfig;
      out = [...out].sort((a, b) => {
        const av = a[field];
        const bv = b[field];
        const an = Number(av);
        const bn = Number(bv);
        let cmp;
        if (!Number.isNaN(an) && !Number.isNaN(bn) && av !== "" && av !== null && bv !== "" && bv !== null) {
          cmp = an - bn;
        } else {
          cmp = String(av === undefined || av === null ? "" : av).localeCompare(
            String(bv === undefined || bv === null ? "" : bv)
          );
        }
        return dir === "desc" ? -cmp : cmp;
      });
    }
    return out;
  }, [data, columnFilters, sortConfig, fieldTypeMap]);

  useEffect(() => {
    processFilter();
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    // Debounced resize + proper cleanup. The old handler added a listener every render with no
    // cleanup, and each resize event churned pageSize -> refetch (a real perf drag, FEAT-032).
    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setWindowSize({ width: window.innerWidth, height: window.innerHeight });
      }, 250);
    };
    window.addEventListener("resize", onResize);
    const fetchDataFunc = async () => {
      if (!loading) {
        await fetchData()
      }
    }
    fetchDataFunc();
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const fetchDataFunc = async () => {
      if (loaded) {
        await fetchData()
      }
    }
    fetchDataFunc();
    // eslint-disable-next-line
  }, [filterData]);

  useEffect(() => {
    // Skip the mount run so we don't reset page/pageSize (and trigger a refetch) on first load —
    // the initial pageSize is already computed in useState. Only respond to actual resizes, and
    // never drop below 25 rows.
    if (!didMountResizeRef.current) {
      didMountResizeRef.current = true;
      return;
    }
    const computed = Math.max(25, parseInt((windowSize.height - 400) / 75, 10) || 25);
    setPageSize((prev) => (prev === computed ? prev : computed));
    setPageSizes(Array.from(new Set([computed, 25, 50, 100, 200])).sort((a, b) => a - b));
    setPage(0);
  }, [windowSize]);

  useEffect(() => {
    let tempTotalPages = Math.ceil(derivedData.length / pageSize);
    // Only advertise a "load more" page when unfiltered — column filters page over the loaded set.
    if (!hasColumnFilters && totalCount > data.length) {
      tempTotalPages += 1
    }
    setTotalPages(tempTotalPages);
    if (derivedData.length > pageSize && pageSize !== 0) {
      const startIndex = page * pageSize;
      setCurrentPageData([...derivedData.slice(startIndex, startIndex + pageSize)]);
    } else {
      setCurrentPageData([...derivedData]);
    }
    // eslint-disable-next-line
  }, [pageSize, derivedData, page, totalCount, hasColumnFilters]);

  return (
    <Container sx={style.dataGridStyleContainer}>
      <DataGridHeader
        title={title}
        dbTable={dbTable}
        toggles={activeToggles}
        processFilter={processFilter}
        addButton={addButton}
        columns={columns}
        addSuccessMessage={addSuccessMessage}
        allData={data}
        setAllData={setData}
        isTab={isTab}
      />

      {filters?.length > 0 && (
        <DataTableFilters filters={filters} processFilter={processFilter} />
      )}
      {loaded ? (
          <DataTable
            data={currentPageData}
            fields={columns}
            handleRowClick={handleRowClick}
            sortConfig={sortConfig}
            onSort={handleSort}
            columnFilters={columnFilters}
            onColumnFilter={handleColumnFilter}
            filterOptionsMap={filterOptionsMap}
          />
      ) : (
        <Container sx={style.dataTableLoadingContainer}>
          <LoadingIcon page="dataTable" />
        </Container>
      )}
                <DataGridFooter
            page={page}
            pageSize={pageSize}
            pageSizes={pageSizes}
            setPage={setPage}
            setPageSize={setPageSize}
            totalPages={totalPages}
            totalRecords={totalCount}
            loadedRecords={data.length}
            title={title}
          />
    </Container>
  );
};

export default DataTableMD;
